// src/features/streams/components/StreamControlsSection.tsx

import { Stack, Select, Switch, NumberInput, Textarea, Divider, Alert } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useController, type Control, type UseFormWatch } from 'react-hook-form';
import {
  EDITABLE_RECORD_MODES,
  RECORD_MODE_LABELS,
  EDITABLE_OFFLINE_MODES,
  OFFLINE_DISPLAY_MODE_LABELS,
  STREAM_CONSTRAINTS,
  type StreamRecordMode,
  type StreamOfflineDisplayMode,
} from '@/lib/constants/stream';
import type { StreamOverviewFormData } from '../schemas/StreamForm.schema';
import type { StreamViewData } from '@/lib/types/Stream';

interface StreamControlsSectionProps {
  control: Control<StreamOverviewFormData>;
  watch: UseFormWatch<StreamOverviewFormData>;
  stream: StreamViewData;
}

export function StreamControlsSection({ control, watch, stream }: StreamControlsSectionProps) {
  // --- Field controllers ---

  const { field: recordModeField } = useController({ control, name: 'recordMode' });
  const { field: recordOverrideField } = useController({ control, name: 'recordOverride' });
  const { field: recordAppendTimeoutField } = useController({ control, name: 'recordAppendTimeout' });
  const { field: hasEmbeddedCaptionsField } = useController({ control, name: 'hasEmbeddedCaptions' });
  const { field: offlineDisplayModeField } = useController({ control, name: 'offlineDisplayMode' });
  const { field: offlineMsgHtmlField, fieldState: offlineMsgHtmlState } = useController({ control, name: 'offlineMsgHtml' });

  const watchedRecordMode = watch('recordMode');
  const watchedOfflineDisplayMode = watch('offlineDisplayMode');
  const offlineMsgLength = watch('offlineMsgHtml')?.length ?? 0;

  // Original stream values for read-only detection
  const originalRecordMode = stream.stream?.record?.recordMode ?? 'manual';
  const originalOfflineMode = stream.stream?.offline?.offlineDisplayMode ?? 'default_message';

  // Schedule recording is read-only
  const isScheduleMode = originalRecordMode === 'schedule';

  // Advanced offline modes are read-only (offline_poster, media, list)
  const isReadOnlyOfflineMode = !EDITABLE_OFFLINE_MODES.includes(originalOfflineMode as StreamOfflineDisplayMode);

  // Build record mode options
  const recordModeOptions = EDITABLE_RECORD_MODES.map((mode) => ({
    value: mode,
    label: RECORD_MODE_LABELS[mode],
  }));

  // Build offline mode options
  const offlineModeOptions = EDITABLE_OFFLINE_MODES.map((mode) => ({
    value: mode,
    label: OFFLINE_DISPLAY_MODE_LABELS[mode],
  }));

  return (
    <Stack gap="md">
      {/* Recording Mode */}
      {isScheduleMode && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          Schedule recording is configured. Editing schedule recording is not yet available.
        </Alert>
      )}
      <Select
        label="Recording Mode"
        data={recordModeOptions}
        value={recordModeField.value}
        onChange={(val) => {
          if (!val) return;
          recordModeField.onChange(val);
          // When switching to automatic, force recordOverride off (legacy behavior)
          if (val === 'automatic') {
            recordOverrideField.onChange(false);
          }
        }}
        onBlur={recordModeField.onBlur}
        disabled={isScheduleMode}
        allowDeselect={false}
      />

      {/* Recording On/Off — only visible in manual mode */}
      {watchedRecordMode === 'manual' && (
        <Switch
          label={recordOverrideField.value ? 'Recording On' : 'Recording Off'}
          checked={recordOverrideField.value}
          onChange={(event) => recordOverrideField.onChange(event.currentTarget.checked)}
          onBlur={recordOverrideField.onBlur}
          color={recordOverrideField.value ? 'green' : 'gray'}
          size="md"
        />
      )}

      {/* New Recording Wait Time */}
      <NumberInput
        label="New Recording Wait Time"
        description="Seconds to wait after encoder disconnects before starting a new recording"
        value={recordAppendTimeoutField.value}
        onChange={(val) => {
          if (typeof val === 'number') {
            recordAppendTimeoutField.onChange(val);
          }
        }}
        onBlur={recordAppendTimeoutField.onBlur}
        suffix=" seconds"
        min={STREAM_CONSTRAINTS.RECORD_APPEND_TIMEOUT_MIN}
        max={STREAM_CONSTRAINTS.RECORD_APPEND_TIMEOUT_MAX}
      />

      <Divider />

      {/* Embedded Captions */}
      <Switch
        label="Use Embedded Stream Captions"
        checked={hasEmbeddedCaptionsField.value}
        onChange={(event) => hasEmbeddedCaptionsField.onChange(event.currentTarget.checked)}
        onBlur={hasEmbeddedCaptionsField.onBlur}
        size="md"
      />

      <Divider />

      {/* Offline Display Mode */}
      {isReadOnlyOfflineMode && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          This offline mode ({OFFLINE_DISPLAY_MODE_LABELS[originalOfflineMode as StreamOfflineDisplayMode]}) is configured but editing is not yet available.
        </Alert>
      )}
      <Select
        label="Offline Display Mode"
        data={offlineModeOptions}
        value={offlineDisplayModeField.value}
        onChange={(val) => {
          if (val) offlineDisplayModeField.onChange(val);
        }}
        onBlur={offlineDisplayModeField.onBlur}
        disabled={isReadOnlyOfflineMode}
        allowDeselect={false}
      />

      {/* Offline Message — visible only for custom_message mode */}
      {watchedOfflineDisplayMode === 'custom_message' && (
        <Textarea
          label="Offline Message"
          description={`Custom message shown when stream is offline. Supports text or HTML. ${offlineMsgLength}/${STREAM_CONSTRAINTS.OFFLINE_MSG_MAX}`}
          value={offlineMsgHtmlField.value}
          onChange={offlineMsgHtmlField.onChange}
          onBlur={offlineMsgHtmlField.onBlur}
          error={offlineMsgHtmlState.error?.message}
          maxLength={STREAM_CONSTRAINTS.OFFLINE_MSG_MAX}
          rows={4}
        />
      )}
    </Stack>
  );
}
